import i18n from "i18next";
import { AssetType } from "verdi-common/Configurations/AssetTypeConfiguration/AssetTypeConfiguration";
import { getAssetOwner } from "@/api/orval/generated/asset-identity";
import type { AssetIdentityWithOwner } from "@/api/orval/generated/schemas";
import { toastManager } from "@/components/ui/toast";
import { NAMESPACES } from "@/i18n/constants";

export const ASSET_ID_PARAM = "assetId";

/** MasterIndex categories that can hold an asset a verdiId resolves to. */
export type MasterIndexAssetCollection = "irrigationDevice" | "blockValve" | "thirdPartyDevice" | "zone" | "aoi";

/**
 * Where each asset type's model lives. Declared over every AssetType so a new one has to be answered
 * here rather than silently falling through to a device lookup.
 *
 * `SensoterraMoisture` is absent because sensoterra devices are never given a verdiId, so nothing can
 * resolve to one. The types with no collection have no client model at all.
 */
const MASTER_INDEX_COLLECTIONS_BY_ASSET_TYPE: Record<AssetType, readonly MasterIndexAssetCollection[]> = {
    [AssetType.DEVICE]: ["irrigationDevice", "blockValve"],
    [AssetType.THIRD_PARTY_DEVICE]: ["thirdPartyDevice"],
    [AssetType.GATEWAY]: ["thirdPartyDevice"],
    [AssetType.ZONE]: ["zone"],
    [AssetType.AREA_OF_INTEREST]: ["aoi"],
    [AssetType.SENSOR]: [],
    [AssetType.VALVE]: [],
    [AssetType.PUMP]: [],
    [AssetType.HYDRAULIC_NODE]: [],
    [AssetType.ORGANIZATION]: [],
};

export type ResolvedAssetDeepLink = {
    identity: AssetIdentityWithOwner;
    collections: readonly MasterIndexAssetCollection[];
};

/** Stashed once the account switch is under way, and taken by the session load that follows it. */
let pendingAssetDeepLink: ResolvedAssetDeepLink | undefined;

function reportUnopenableAsset(assetId: string) {
    toastManager.add({
        title: i18n.t("asset-link.failed.title", { ns: NAMESPACES.COMMON, assetId }),
        description: i18n.t("asset-link.failed.description", { ns: NAMESPACES.COMMON }),
        type: "error",
    });
}

/** Uppercased to match the stored form. Separator tolerance belongs to a typed-in id, not a URL. */
function readAssetIdParam(): string | undefined {
    return new URLSearchParams(window.location.search).get(ASSET_ID_PARAM)?.trim().toUpperCase() || undefined;
}

function toDeepLink(identity: AssetIdentityWithOwner): ResolvedAssetDeepLink | undefined {
    // Record<AssetType, …> is only a compile-time guarantee, and assetType arrives over the network.
    // A type the backend added since this build must not throw here — it aborts the session load.
    const collections = MASTER_INDEX_COLLECTIONS_BY_ASSET_TYPE[identity.assetType as AssetType];
    return collections?.length ? { identity, collections } : undefined;
}

/**
 * Resolves the `assetId` query parameter to the organization that owns it, so the caller can switch
 * accounts before the session loads. Returns undefined when there is no parameter, or when it names
 * something this dashboard cannot open — which is reported to the user rather than swallowed.
 *
 * Resolving does not stash anything. The caller stashes once the account switch is under way, so a
 * failed switch cannot leave the link waiting for some other organization's session load to eat.
 */
export async function resolveAssetDeepLink(): Promise<ResolvedAssetDeepLink | undefined> {
    const assetId = readAssetIdParam();
    if (!assetId) {
        return undefined;
    }

    const identity = await getAssetOwner(assetId).catch((e) => {
        console.warn(`Could not resolve asset ${assetId}`, e);
        return undefined;
    });
    const deepLink = identity && toDeepLink(identity);
    if (!deepLink) {
        reportUnopenableAsset(assetId);
        return undefined;
    }

    return deepLink;
}

/** Called once the asset's account is loading, so the session load that follows opens the card. */
export function stashAssetDeepLink(deepLink: ResolvedAssetDeepLink) {
    pendingAssetDeepLink = deepLink;
}

/**
 * Hands over the stashed deep link and forgets it. `doInitialMapPan` runs on every session load, so
 * without this a later account switch would pull the user back to the linked asset.
 */
export function takePendingAssetDeepLink(): ResolvedAssetDeepLink | undefined {
    const pending = pendingAssetDeepLink;
    pendingAssetDeepLink = undefined;
    return pending;
}

export function reportAssetDeepLinkFailed(assetId: string) {
    reportUnopenableAsset(assetId);
}
